import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { upsertShortstay, listenToShortstayData } from "../../../../redux/shortstaysSlice";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Settings2, ShieldCheck, DollarSign, Zap, UserCheck, AlertTriangle } from "lucide-react";

/* ── tokens ── */
const T = {
  bg: "#F1EFE8", surface: "#ffffff", border: "#D3D1C7",
  border2: "#B4B2A9", text: "#2C2C2A", textSoft: "#5F5E5A",
  muted: "#888780", accent: "#2C2C2A", accentFg: "#F1EFE8",
  warn: "#FAEEDA", warnText: "#633806",
  sans: "'Montserrat', sans-serif",
};

type BookingType = "instant" | "approve";
type PolicyType  = "system" | "free" | "partial";

const BOOKING_CARDS: {
  id: BookingType; title: string; desc: string;
  icon: React.ReactNode; iconBg: string; recommended?: boolean;
}[] = [
  {
    id: "instant", recommended: true,
    title: "Instant booking",
    desc: "Guests book immediately — no approval needed. Higher visibility and more reservations on average.",
    icon: <Zap size={20} color="#5F5E5A" />, iconBg: "#F1EFE8",
  },
  {
    id: "approve",
    title: "Approve bookings",
    desc: "Review each request before confirming. You have 24 hours to accept or decline before it expires.",
    icon: <UserCheck size={20} color="#5F5E5A" />, iconBg: "#F1EFE8",
  },
];

const POLICY_CARDS: {
  id: PolicyType; title: string; desc: string;
  icon: React.ReactNode; iconBg: string;
}[] = [
  {
    id: "system", title: "System default",
    desc: "Whoever cancels compensates one night's stay.",
    icon: <Settings2 size={16} color="#5F5E5A" />, iconBg: "#F1EFE8",
  },
  {
    id: "free", title: "Free cancellation",
    desc: "Guest receives a full refund if they cancel.",
    icon: <ShieldCheck size={16} color="#3B6D11" />, iconBg: "#EAF3DE",
  },
  {
    id: "partial", title: "Partial (50%)",
    desc: "Guest and host each keep half the booking value.",
    icon: <DollarSign size={16} color="#BA7517" />, iconBg: "#FAEEDA",
  },
];

const ShortBookingSettings: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const currentListing = useSelector((s: RootState) => s.shortstays.currentListing);

  const [bookingType,     setBookingType]     = useState<BookingType>("instant");
  const [originalBooking, setOriginalBooking] = useState<BookingType>("instant");
  const [policy,          setPolicy]          = useState<PolicyType>("system");
  const [originalPolicy,  setOriginalPolicy]  = useState<PolicyType>("system");
  const [showModal,       setShowModal]       = useState(false);
  const [isSaving,        setIsSaving]        = useState(false);
  const [success,         setSuccess]         = useState(false);

  const hasChanges = bookingType !== originalBooking || policy !== originalPolicy;

  useEffect(() => {
    if (listingId) dispatch(listenToShortstayData(listingId));
  }, [dispatch, listingId]);

  useEffect(() => {
    if (currentListing) {
      /* booking type */
      const bt = currentListing.bookingType;
      const loadedBt: BookingType = bt?.approveReservations ? "approve" : "instant";
      setBookingType(loadedBt);
      setOriginalBooking(loadedBt);

      /* cancellation policy */
      const cp = currentListing.cancellationPolicy;
      const loadedCp: PolicyType = cp?.free ? "free" : cp?.partial ? "partial" : "system";
      setPolicy(loadedCp);
      setOriginalPolicy(loadedCp);
    }
  }, [currentListing]);

  const handleBookingSelect = (type: BookingType) => {
    if (type === "approve") { setShowModal(true); return; }
    setBookingType("instant");
  };

  const confirmApprove = () => { setBookingType("approve"); setShowModal(false); };

  const handleSave = async () => {
    if (!listingId || !hasChanges) return;
    setIsSaving(true);
    try {
      await dispatch(upsertShortstay({
        listingId,
        data: {
          bookingType: {
            instantReservations: bookingType === "instant",
            approveReservations: bookingType === "approve",
          },
          cancellationPolicy: {
            free:    policy === "free",
            partial: policy === "partial",
            system:  policy === "system",
          },
        },
      }));
      setOriginalBooking(bookingType);
      setOriginalPolicy(policy);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 1500);
    } catch {
      alert("Failed to save settings.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={s.page}>

      {/* saving overlay */}
      {(isSaving || success) && (
        <div style={s.overlay}>
          {isSaving && !success && <CircularProgress size={52} sx={{ color: T.accent }} />}
          {success && <CheckCircleOutlineIcon sx={{ fontSize: 64, color: "#3B6D11" }} />}
        </div>
      )}

      {/* warning modal */}
      {showModal && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={s.modalIconWrap}>
              <AlertTriangle size={22} color="#BA7517" />
            </div>
            <p style={s.modalTitle}>Instant booking is recommended</p>
            <p style={s.modalBody}>
              Our algorithm favours listings with instant booking enabled.
              Switching to manual approval typically reduces booking frequency.
            </p>
            <div style={s.algoBox}>
              <strong style={{ color: T.text, fontWeight: 500 }}>
                Why instant booking performs better
              </strong>
              <br />
              Listings with instant booking appear higher in search, receive 3×
              more views on average, and convert at a significantly higher rate
              than request-based listings.
            </div>
            <p style={{ ...s.modalBody, marginTop: 12 }}>
              Are you sure you want to require manual approval?
            </p>
            <div style={s.modalActions}>
              <button onClick={() => setShowModal(false)} style={s.btnOutline}>Keep instant</button>
              <button onClick={confirmApprove} style={s.btnDark}>Yes, use approval</button>
            </div>
          </div>
        </div>
      )}

      <div style={s.container}>

        {/* heading */}
        <p style={s.stepLabel}>Listing settings</p>
        <p style={s.title}>Booking settings</p>
        <p style={s.subtitle}>Manage how guests book your property and your cancellation terms.</p>

        {/* ── booking type ── */}
        <p style={s.sectionLabel}>Booking type</p>
        <div style={s.cardGrid}>
          {BOOKING_CARDS.map(({ id, title, desc, icon, iconBg, recommended }) => {
            const active = bookingType === id;
            return (
              <div
                key={id}
                onClick={() => handleBookingSelect(id)}
                style={{ ...s.bcard, ...(active ? s.bcardActive : {}) }}
              >
                {recommended && <span style={s.badge}>Recommended</span>}
                <div style={{ ...s.checkWrap, opacity: active ? 1 : 0 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 14, color: T.accentFg }} />
                </div>
                <div style={{ ...s.iconWrap, background: iconBg }}>{icon}</div>
                <p style={s.cardTitle}>{title}</p>
                <p style={s.cardDesc}>{desc}</p>
              </div>
            );
          })}
        </div>

        {/* ── cancellation policy ── */}
        <p style={{ ...s.sectionLabel, marginTop: 28 }}>Cancellation policy</p>
        <div style={s.cancelGrid}>
          {POLICY_CARDS.map(({ id, title, desc, icon, iconBg }) => {
            const active = policy === id;
            return (
              <div
                key={id}
                onClick={() => setPolicy(id)}
                style={{ ...s.pcard, ...(active ? s.pcardActive : {}) }}
              >
                <div style={{ ...s.checkWrap, opacity: active ? 1 : 0 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 14, color: T.accentFg }} />
                </div>
                <div style={{ ...s.pIconWrap, background: iconBg }}>{icon}</div>
                <p style={s.pcardTitle}>{title}</p>
                <p style={s.pcardDesc}>{desc}</p>
              </div>
            );
          })}
        </div>

        {/* save */}
        <button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          style={{
            ...s.saveBtn,
            ...(hasChanges && !isSaving ? s.saveBtnActive : s.saveBtnDisabled),
          }}
        >
          {isSaving ? "Saving…" : success ? "Saved ✓" : "Save settings"}
        </button>

      </div>
    </div>
  );
};

export default ShortBookingSettings;

/* ── styles ── */
const s: Record<string, React.CSSProperties> = {
  overlay:        { position: "fixed", inset: 0, background: "rgba(241,239,232,.85)", backdropFilter: "blur(6px)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
  container:      { maxWidth: 860, margin: "0 auto", padding: "48px 20px 80px" },

  stepLabel:      { fontSize: 10, fontWeight: 500, color: T.muted, letterSpacing: "0.15em", textTransform: "uppercase" as const, marginBottom: 6 },
  title:          { fontSize: 24, fontWeight: 500, color: T.text, marginBottom: 4 },
  subtitle:       { fontSize: 13, color: T.textSoft, marginBottom: 32 },
  sectionLabel:   { fontSize: 11, fontWeight: 500, color: T.muted, letterSpacing: "0.1em", textTransform: "uppercase" as const, marginBottom: 12 },

  /* booking cards */
  cardGrid:       { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 },
  bcard:          { background: T.surface, border: `1.5px solid ${T.border}`, borderRadius: 12, padding: 20, cursor: "pointer", position: "relative", transition: "border-color .2s" },
  bcardActive:    { borderColor: T.accent, boxShadow: "0 0 0 3px rgba(44,44,42,.1)" },
  badge:          { display: "inline-block", fontSize: 10, fontWeight: 500, background: T.bg, color: T.muted, border: `0.5px solid ${T.border}`, borderRadius: 6, padding: "2px 8px", marginBottom: 10, letterSpacing: "0.04em" },
  iconWrap:       { width: 40, height: 40, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 },
  cardTitle:      { fontSize: 14, fontWeight: 500, color: T.text, marginBottom: 4 },
  cardDesc:       { fontSize: 12, color: T.textSoft, lineHeight: 1.5 },

  /* policy cards */
  cancelGrid:     { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10 },
  pcard:          { background: T.surface, border: `1.5px solid ${T.border}`, borderRadius: 12, padding: 16, cursor: "pointer", position: "relative", transition: "border-color .2s" },
  pcardActive:    { borderColor: T.accent, boxShadow: "0 0 0 3px rgba(44,44,42,.1)" },
  pIconWrap:      { width: 34, height: 34, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 },
  pcardTitle:     { fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 3 },
  pcardDesc:      { fontSize: 11, color: T.textSoft, lineHeight: 1.5 },

  /* shared check badge */
  checkWrap:      { position: "absolute", top: 12, right: 12, width: 20, height: 20, borderRadius: "50%", background: T.accent, display: "flex", alignItems: "center", justifyContent: "center", transition: "opacity .2s" },

  /* modal */
  modal:          { background: T.surface, border: `0.5px solid ${T.border}`, borderRadius: 16, padding: 28, maxWidth: 400, width: "100%" },
  modalIconWrap:  { width: 48, height: 48, borderRadius: "50%", background: T.warn, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 },
  modalTitle:     { fontSize: 16, fontWeight: 500, color: T.text, marginBottom: 8 },
  modalBody:      { fontSize: 13, color: T.textSoft, lineHeight: 1.6 },
  algoBox:        { background: T.bg, borderRadius: 8, padding: 12, margin: "12px 0", fontSize: 12, color: T.textSoft, lineHeight: 1.7, borderLeft: `3px solid ${T.border2}` },
  modalActions:   { display: "flex", gap: 10, marginTop: 18 },
  btnOutline:     { flex: 1, padding: "10px", borderRadius: 8, background: "transparent", color: T.textSoft, border: `0.5px solid ${T.border2}`, cursor: "pointer", fontSize: 13, fontFamily: T.sans },
  btnDark:        { flex: 1, padding: "10px", borderRadius: 8, background: T.accent, color: T.accentFg, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 500, fontFamily: T.sans },

  /* save button */
  saveBtn:        { marginTop: 28, width: "100%", padding: "13px 0", borderRadius: 8, border: "none", fontSize: 13, fontWeight: 500, fontFamily: T.sans, letterSpacing: "0.06em", transition: "background .2s" },
  saveBtnActive:  { background: T.accent, color: T.accentFg, cursor: "pointer" },
  saveBtnDisabled:{ background: T.border, color: T.muted, cursor: "not-allowed" },
};